import { GrayButton, RedButton } from 'components/Button'
import I18n from 'components/I18n'
import { parseSymbol } from 'data/Instrument/types'
import { OrderType } from 'data/types'
import messages from 'lang'
import * as React from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import webSocket from 'services/api/webSocket'

import OrderDetailsTable from '../OrderConfirmation/OrderDetails'
import {
  Footer,
  getOrderTypeString,
  Header,
  HeaderItem,
  TIF_TEXT_MAP,
  Wrapper
} from '../OrderConfirmation/utils'
import { PropsType as _P } from '.'

const getFees = (order, userFees, intl, precision) => {
  const orderFee = order.fee
  const digits = precision.price
  const { counter } = parseSymbol(order.symbol)

  if (orderFee !== undefined) {
    const amount = intl.formatNumber(orderFee, {
      maximumFractionDigits: digits,
      minimumFractionDigits: digits
    })
    return `${amount} ${counter}`
  }

  const userActiveFees = userFees[order.symbol].active
  if (userActiveFees) {
    const maker = intl.formatMessage(messages.fee_percentage_maker, {
      value: userActiveFees.makerFee / 100
    })
    const taker = intl.formatMessage(messages.fee_percentage_taker, {
      value: userActiveFees.takerFee / 100
    })
    return `${maker} / ${taker}`
  }

  return '-'
}

type PropsType = _P & WrappedComponentProps

const PARTIL_STATUS = [
  webSocket.order.status.PARTIAL.toLocaleLowerCase(),
  webSocket.order.status.PART_FILLED.toLocaleLowerCase()
]
const isPartialOrder = ({ ordStatus }: OrderType) =>
  PARTIL_STATUS.indexOf(ordStatus.toLocaleLowerCase()) > -1

const OrderDetails = ({
  canTrade,
  cancel,
  hideModal,
  intl,
  order,
  precision,
  userFees
}: PropsType) => {
  if (!order) return null
  return (
    <Wrapper>
      <Header>
        <HeaderItem side={order.side}>
          <span>{order.symbol}</span>
          <span>
            {order.side} {getOrderTypeString(order.ordType)}
          </span>
        </HeaderItem>
        {order.marginOrder ? (
          <HeaderItem side={order.side}>
            <span>
              <I18n data={messages.margin_leverage} />
            </span>
            <span>{order.leverageRatio}X</span>
          </HeaderItem>
        ) : order && order.orderID !== '0' ? (
          <HeaderItem side={order.side}>
            <span>
              <I18n data={messages.title_order_details} order_id={order.orderID} />
            </span>
          </HeaderItem>
        ) : null}
      </Header>
      <OrderDetailsTable
        status={webSocket.order.getStatus(order)}
        amount={order.orderQty}
        fees={getFees(order, userFees, intl, precision)}
        fillPrice={order.avgPx}
        limitPrice={
          order.ordType === webSocket.order.type.LIMIT ||
          order.ordType === webSocket.order.type.STOP_LIMIT
            ? order.price
            : undefined
        }
        remaining={isPartialOrder(order) ? order.leavesQty : undefined}
        symbol={order.symbol}
        tif={
          order.ordType !== webSocket.order.type.MARKET
            ? TIF_TEXT_MAP[order.timeInForce]
            : undefined
        }
        time={order.sendingTime ?? order.created}
        triggerPrice={
          order.ordType === webSocket.order.type.STOP_LIMIT ||
          order.ordType === webSocket.order.type.STOP
            ? order.stopPx
            : undefined
        }
        type={getOrderTypeString(order.ordType)}
      />
      <Footer>
        {order && webSocket.order.isCancellable(order) && canTrade && (
          <RedButton e2e='confirmationCancelOrderButton' onClick={() => cancel(order.orderID)}>
            <I18n data={messages.order_confirmation_cancel} />
          </RedButton>
        )}
        <GrayButton e2e='confirmationCancelOrderButton' onClick={hideModal}>
          <I18n data={messages.button_close} />
        </GrayButton>
      </Footer>
    </Wrapper>
  )
}

export default injectIntl(OrderDetails)
